// Admin · Notes. This file and server/admin_notes.go are the whole section.
//
// A shared scratchpad for the two principals: one row per note, newest first,
// with a compose box that is always on screen because writing one is the point
// of the page rather than a mode you enter.
//
// PLAIN TEXT, ALWAYS. Note bodies are written with textContent and never with
// innerHTML — no markdown, no HTML. The two people who can reach this page are
// trusted, but "we trust each other" is not "no one else will ever hold a
// session", and rendering stored markup into an authenticated admin page buys a
// foothold for nothing. Line breaks survive because .ad-note-body is pre-wrap,
// which is all the formatting a development note has ever needed.
//
// DONE, NOT DELETED. done_at exists so a settled note keeps its context: "we
// decided against this" is worth as much in six months as "we did it". Done
// notes therefore stay in the list, struck through under .ad-done, and can be
// undone. Delete exists as the rarer, deliberate action behind a confirm — for
// the note that should never have been written down, not for the finished one.
//
// WHERE THE DONE ONES GO: below the open ones, and behind a toggle that starts
// off. Two reasons, in this order — the open notes are the working list and a
// month of finished ones pushes them under the fold; and keeping done notes as
// their own group, rather than interleaved by date, makes "what is still
// outstanding" answerable by looking at the top of the page instead of reading
// every row. The toggle says how many are hidden, so nothing silently vanishes.

interface Note {
  id: number;
  /** null when the account that wrote it has been removed (author_id is
   *  ON DELETE SET NULL — a note outlives its author). */
  author: string | null;
  /** Whether the CALLER wrote it: decides only whether Edit is offered. The
   *  server re-checks authorship on every PATCH, so a browser that lies about
   *  this gets a button and a 403. */
  mine: boolean;
  created_at: string;
  updated_at: string;
  done_at: string | null;
  body: string;
}

type Fetched<T> = { ok: true; data: T } | { ok: false; error: string };

const MAX_NOTE_LEN = 8000;

/** A note counts as edited only when updated_at has moved clearly past
 *  created_at. Both are set by the same now() on insert, but a margin keeps a
 *  sub-second difference in the row's own two clocks from labelling an
 *  untouched note "edited" — the label is only worth having if it is never
 *  wrong. */
const EDITED_MARGIN_MS = 2000;

// --- small helpers ----------------------------------------------------------

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (className !== undefined) n.className = className;
  if (text !== undefined) n.textContent = text;
  return n;
}

function button(label: string, className: string, onClick: () => void): HTMLButtonElement {
  const b = el("button", className, label);
  b.type = "button";
  b.addEventListener("click", onClick);
  return b;
}

const pad = (n: number): string => String(n).padStart(2, "0");

/** "2026-08-15 19:32 UTC" — the one clock both principals share. */
function fmtUtc(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return (
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}` +
    ` ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`
  );
}

/** "just now" / "12 min ago" / "3 h ago" / "6 d ago", then a bare date. The
 *  absolute stamp is printed beside it, so this only has to answer "is this
 *  fresh?" at a glance. A timestamp in the future (two machines, two clocks)
 *  reads as "just now" rather than a negative age. */
function fmtRelative(iso: string, now: number): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const secs = Math.round((now - t) / 1000);
  if (secs < 45) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} d ago`;
  return fmtUtc(iso).slice(0, 10);
}

// --- the API, narrowed ------------------------------------------------------

function asRecord(v: unknown): Record<string, unknown> | null {
  return typeof v === "object" && v !== null && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

function asNote(raw: unknown): Note | null {
  const r = asRecord(raw);
  if (r === null) return null;
  if (typeof r.id !== "number" || typeof r.body !== "string") return null;
  if (typeof r.created_at !== "string" || typeof r.updated_at !== "string") return null;
  return {
    id: r.id,
    author: typeof r.author === "string" ? r.author : null,
    mine: r.mine === true,
    created_at: r.created_at,
    updated_at: r.updated_at,
    done_at: typeof r.done_at === "string" ? r.done_at : null,
    body: r.body,
  };
}

/** One request. Nothing throws across this boundary: a dead endpoint or a
 *  wrong-shaped body degrades into { ok: false, error }, never an exception
 *  into a blank section. The server's own message is preferred when it sent
 *  one — "a note needs some text" is more use than "400". */
async function request<T>(
  url: string,
  init: RequestInit,
  parse: (raw: unknown) => T | null,
): Promise<Fetched<T>> {
  let res: Response;
  try {
    res = await fetch(url, { credentials: "same-origin", ...init });
  } catch {
    return { ok: false, error: "could not reach the server" };
  }
  if (res.status === 204) {
    const empty = parse(null);
    return empty === null ? { ok: false, error: "unexpected response" } : { ok: true, data: empty };
  }
  let raw: unknown = null;
  try {
    raw = await res.json();
  } catch {
    raw = null;
  }
  if (!res.ok) {
    const r = asRecord(raw);
    const msg = typeof r?.error === "string" ? r.error : `request failed (${res.status})`;
    return { ok: false, error: msg };
  }
  const data = parse(raw);
  return data === null ? { ok: false, error: "unexpected response" } : { ok: true, data };
}

function fetchNotes(): Promise<Fetched<Note[]>> {
  return request("/api/admin/notes", { method: "GET" }, (raw) => {
    const list = asRecord(raw)?.notes;
    if (!Array.isArray(list)) return null;
    const out: Note[] = [];
    for (const item of list) {
      const n = asNote(item);
      if (n !== null) out.push(n);
    }
    return out;
  });
}

function jsonInit(method: string, body: unknown): RequestInit {
  return {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  };
}

function postNote(body: string): Promise<Fetched<Note>> {
  return request("/api/admin/notes", jsonInit("POST", { body }), asNote);
}

function patchNote(id: number, patch: { body?: string; done?: boolean }): Promise<Fetched<Note>> {
  return request(`/api/admin/notes/${id}`, jsonInit("PATCH", patch), asNote);
}

function deleteNote(id: number): Promise<Fetched<true>> {
  return request(`/api/admin/notes/${id}`, { method: "DELETE" }, () => true);
}

// --- the section ------------------------------------------------------------

export function mountAdminNotes(host: HTMLElement): void {
  let notes: Note[] = [];
  let phase: "loading" | "ready" | "error" = "loading";
  let loadError = "";
  let showDone = false;
  /** The note being edited, if any. Held here rather than in the DOM so a
   *  re-render (a save elsewhere, a refresh) cannot lose half-typed text. */
  let editingID: number | null = null;
  let editDraft = "";
  /** Ids with a request in flight, so a second click cannot fire a second
   *  write against a row that is already changing. */
  const busy = new Set<number>();

  // --- compose --------------------------------------------------------------

  const composeCard = el("section", "ad-card");
  composeCard.appendChild(el("h2", undefined, "New note"));
  const composeBody = el("div", "ad-body");
  const box = el("textarea", "fbbox");
  box.rows = 4;
  box.placeholder = "What did you find, decide, or want the other one to know?";
  box.setAttribute("aria-label", "New note");
  box.maxLength = MAX_NOTE_LEN;
  const composeBar = el("div", "ad-toolbar");
  const composeStatus = el("span", "ad-when");
  const addBtn = button("Add note", "btn primary", () => {
    void submit();
  });
  composeBar.append(addBtn, composeStatus);
  composeBody.append(box, composeBar);
  composeCard.appendChild(composeBody);

  async function submit(): Promise<void> {
    const text = box.value.trim();
    if (text === "") {
      composeStatus.textContent = "Nothing to save — the note is empty.";
      box.focus();
      return;
    }
    addBtn.disabled = true;
    composeStatus.textContent = "Saving…";
    const res = await postNote(text);
    addBtn.disabled = false;
    if (!res.ok) {
      // The typed text stays in the box: losing a paragraph to a failed request
      // is the one thing a scratchpad must never do.
      composeStatus.textContent = `Could not save — ${res.error}`;
      return;
    }
    box.value = "";
    composeStatus.textContent = "Saved.";
    notes = [res.data, ...notes];
    // A note written now is by definition not done, so it lands at the top of
    // the open list where the writer is already looking.
    renderList();
  }

  // Ctrl/Cmd+Enter submits — the box is multi-line, so plain Enter belongs to
  // the text.
  box.addEventListener("keydown", (ev) => {
    if (ev.key === "Enter" && (ev.metaKey || ev.ctrlKey)) {
      ev.preventDefault();
      void submit();
    }
  });

  // --- list -----------------------------------------------------------------

  const listCard = el("section", "ad-card");
  listCard.appendChild(el("h2", undefined, "Notes"));
  const listBody = el("div", "ad-body");
  const listBar = el("div", "ad-toolbar");
  const doneToggle = button("Show done", "btn", () => {
    showDone = !showDone;
    renderList();
  });
  const refreshBtn = button("Refresh", "btn", () => {
    void load();
  });
  const listStatus = el("span", "ad-when");
  const spacer = el("span", "ad-spacer");
  listBar.append(doneToggle, refreshBtn, spacer, listStatus);
  const listHost = el("div");
  listBody.append(listBar, listHost);
  listCard.appendChild(listBody);

  host.append(composeCard, listCard);

  function metaCell(n: Note, now: number): HTMLTableCellElement {
    const td = el("td", "mono");
    td.appendChild(el("div", undefined, n.author ?? "account removed"));
    td.appendChild(el("div", "ad-when", fmtRelative(n.created_at, now)));
    td.appendChild(el("div", "ad-when", fmtUtc(n.created_at)));
    const edited =
      new Date(n.updated_at).getTime() - new Date(n.created_at).getTime() > EDITED_MARGIN_MS;
    if (edited) {
      // Said out loud, with its own time: a changed note must not be able to
      // read as the one that was originally written.
      td.appendChild(el("div", "ad-when", `edited ${fmtUtc(n.updated_at)}`));
    }
    if (n.done_at !== null) {
      td.appendChild(el("div", "ad-when", `done ${fmtUtc(n.done_at)}`));
    }
    return td;
  }

  function bodyCell(n: Note): HTMLTableCellElement {
    const td = el("td");
    if (editingID === n.id) {
      const edit = el("textarea", "fbbox");
      edit.rows = 4;
      edit.value = editDraft;
      edit.maxLength = MAX_NOTE_LEN;
      edit.setAttribute("aria-label", "Edit note");
      edit.addEventListener("input", () => {
        editDraft = edit.value;
      });
      const bar = el("div", "ad-toolbar");
      const status = el("span", "ad-when");
      const save = button("Save", "btn primary", () => {
        void (async () => {
          const text = edit.value.trim();
          if (text === "") {
            status.textContent = "A note needs some text.";
            return;
          }
          save.disabled = true;
          status.textContent = "Saving…";
          const res = await patchNote(n.id, { body: text });
          save.disabled = false;
          if (!res.ok) {
            status.textContent = `Could not save — ${res.error}`;
            return;
          }
          replace(res.data);
          editingID = null;
          editDraft = "";
          renderList();
        })();
      });
      const cancel = button("Cancel", "btn", () => {
        editingID = null;
        editDraft = "";
        renderList();
      });
      bar.append(save, cancel, status);
      td.append(edit, bar);
      // Autofocus at the end of the existing text, so editing starts where
      // typing would continue.
      queueMicrotask(() => {
        edit.focus();
        edit.setSelectionRange(edit.value.length, edit.value.length);
      });
      return td;
    }
    // textContent, never innerHTML: the body is text, and .ad-note-body keeps
    // its line breaks (pre-wrap).
    td.appendChild(el("div", "ad-note-body", n.body));
    return td;
  }

  function actionsCell(n: Note): HTMLTableCellElement {
    const td = el("td");
    const disabled = busy.has(n.id) || editingID === n.id;

    const doneBtn = button(n.done_at === null ? "Done" : "Undo", "btn", () => {
      void toggleDone(n);
    });
    doneBtn.disabled = disabled;
    td.appendChild(doneBtn);

    if (n.mine) {
      const editBtn = button("Edit", "btn", () => {
        editingID = n.id;
        editDraft = n.body;
        renderList();
      });
      editBtn.disabled = busy.has(n.id);
      td.append(" ", editBtn);
    }

    const delBtn = button("Delete", "btn", () => {
      void remove(n);
    });
    delBtn.disabled = disabled;
    td.append(" ", delBtn);
    return td;
  }

  function replace(updated: Note): void {
    notes = notes.map((n) => (n.id === updated.id ? updated : n));
  }

  async function toggleDone(n: Note): Promise<void> {
    busy.add(n.id);
    renderList();
    const res = await patchNote(n.id, { done: n.done_at === null });
    busy.delete(n.id);
    if (!res.ok) {
      listStatus.textContent = `Could not update — ${res.error}`;
      renderList();
      return;
    }
    listStatus.textContent = "";
    replace(res.data);
    renderList();
  }

  async function remove(n: Note): Promise<void> {
    const preview = n.body.length > 60 ? `${n.body.slice(0, 60)}…` : n.body;
    // Deliberate, and irreversible — done is the ordinary way a note ends, so
    // this one asks first and quotes what is about to go.
    if (!confirm(`Delete this note for good?\n\n${preview}`)) return;
    busy.add(n.id);
    renderList();
    const res = await deleteNote(n.id);
    busy.delete(n.id);
    if (!res.ok) {
      listStatus.textContent = `Could not delete — ${res.error}`;
      renderList();
      return;
    }
    listStatus.textContent = "";
    notes = notes.filter((x) => x.id !== n.id);
    if (editingID === n.id) editingID = null;
    renderList();
  }

  function table(rows: readonly Note[], now: number): HTMLTableElement {
    const t = el("table", "ad-table");
    const tbody = el("tbody");
    for (const n of rows) {
      const tr = el("tr", n.done_at === null ? undefined : "ad-done");
      tr.append(metaCell(n, now), bodyCell(n), actionsCell(n));
      tbody.appendChild(tr);
    }
    t.appendChild(tbody);
    return t;
  }

  function renderList(): void {
    listHost.replaceChildren();

    if (phase === "loading") {
      // Three states, three sentences. "No notes" and "could not load notes"
      // must never look the same: one is a page working, the other is a page
      // lying.
      listHost.appendChild(el("p", "ad-empty", "Loading notes…"));
      doneToggle.disabled = true;
      return;
    }
    if (phase === "error") {
      listHost.appendChild(el("p", "ad-empty", `Could not load notes — ${loadError}`));
      doneToggle.disabled = true;
      return;
    }

    const open = notes.filter((n) => n.done_at === null);
    const done = notes.filter((n) => n.done_at !== null);
    // Nothing done left (the last one was undone or deleted): drop back to the
    // closed state, or the control sits there reading "Hide done (0)".
    if (done.length === 0) showDone = false;
    doneToggle.disabled = done.length === 0;
    doneToggle.textContent = showDone
      ? `Hide done (${done.length})`
      : `Show done (${done.length})`;

    if (notes.length === 0) {
      listHost.appendChild(el("p", "ad-empty", "Nothing yet — write the first one."));
      return;
    }

    const now = Date.now();
    if (open.length === 0) {
      listHost.appendChild(el("p", "ad-empty", "Nothing open — everything here is done."));
    } else {
      listHost.appendChild(table(open, now));
    }
    if (showDone && done.length > 0) {
      // A label rather than a heading: .ad-card > h2 only styles a card's own
      // title, and a bare <h2> inside the body would arrive at browser-default
      // size. This section adds no CSS, so it uses the muted class that exists.
      listHost.append(el("p", "ad-empty", `Done (${done.length})`), table(done, now));
    }
  }

  async function load(): Promise<void> {
    phase = "loading";
    refreshBtn.disabled = true;
    renderList();
    const res = await fetchNotes();
    refreshBtn.disabled = false;
    if (!res.ok) {
      phase = "error";
      loadError = res.error;
      renderList();
      return;
    }
    notes = res.data;
    phase = "ready";
    // A note being edited that has since been deleted elsewhere would otherwise
    // leave an editor attached to nothing.
    if (editingID !== null && !notes.some((n) => n.id === editingID)) editingID = null;
    renderList();
  }

  void load();
}
